import React, { useState } from 'react';
import { postChatFeedback } from '../../api/dashboardApi';
import { CHAT_FEEDBACK_COMMENT_MAX, CHAT_FEEDBACK_REASONS } from '../../constants/chatFeedback';

/**
 * Face icons (owner's choice 2026-10-28, instead of 👍/👎 which reads harsh in Japanese UI):
 * outline in its own colour until picked, then a solid disc with the face knocked out in white.
 */
function Face({ kind, on = false }) {
  const features =
    kind === 'happy' ? (
      <>
        <path d="M13 21q4-5 8 0M27 21q4-5 8 0" fill="none" stroke={on ? '#fff' : 'currentColor'} strokeWidth="3" strokeLinecap="round" />
        <path d="M16 28q8 9 16 0z" fill={on ? '#fff' : 'currentColor'} />
      </>
    ) : (
      <>
        <path d="M14 17l6 6M20 17l-6 6M28 17l6 6M34 17l-6 6" fill="none" stroke={on ? '#fff' : 'currentColor'} strokeWidth="3" strokeLinecap="round" />
        <ellipse cx="24" cy="32" rx="3.5" ry="5" fill={on ? '#fff' : 'currentColor'} />
      </>
    );
  return (
    <svg viewBox="0 0 48 48" width="22" height="22" aria-hidden="true">
      {on ? (
        <circle cx="24" cy="24" r="22" fill="currentColor" />
      ) : (
        <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="3.2" />
      )}
      {features}
    </svg>
  );
}

/**
 * 👍 / 👎 under one AI reply. 👍 saves at once and shows nothing more; 👎 opens the reason chips
 * + ひと言 and saves on 送信 (a reason or a comment is required). Pressing the other side later
 * re-rates the same reply (the server overwrites). Only one side is ever shown as selected.
 * Task: githubDOCS/TASKS/2026-10-27_AI相談の回答に評価ボタン.md
 */
export default function ReplyFeedback({ sessionId, reply, question }) {
  const [rating, setRating] = useState(null); // saved rating: 'up' | 'down' | null
  const [open, setOpen] = useState(false); // 👎 form showing
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!sessionId || !reply) return null;

  const save = async (next, picked = [], note = '') => {
    setSaving(true);
    setError(null);
    try {
      await postChatFeedback(sessionId, { rating: next, reasons: picked, comment: note, reply, question });
      setRating(next);
      setOpen(false);
    } catch (err) {
      setError(err.message || '評価を保存できませんでした');
    } finally {
      setSaving(false);
    }
  };

  const toggleReason = (r) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  // while the 👎 form is open, 👎 is the active side regardless of what was saved before
  const upOn = rating === 'up' && !open;
  const downOn = rating === 'down' || open;
  const canSend = reasons.length > 0 || comment.trim().length > 0;

  return (
    <div className="rfb">
      <div className="rfb-row">
        <button
          type="button"
          className={`rfb-btn rfb-btn--good${upOn ? ' is-on' : ''}`}
          aria-label="良かった"
          aria-pressed={upOn}
          disabled={saving}
          onClick={() => {
            setOpen(false);
            if (rating !== 'up') save('up');
          }}
        >
          <Face kind="happy" on={upOn} />
        </button>
        <button
          type="button"
          className={`rfb-btn rfb-btn--bad${downOn ? ' is-on' : ''}`}
          aria-label="いまいち"
          aria-pressed={downOn}
          disabled={saving}
          onClick={() => setOpen((v) => !v)}
        >
          <Face kind="dizzy" on={downOn} />
        </button>
      </div>
      {open && (
        <div className="rfb-form">
          <div className="rfb-q">どこが良くなかったですか？（複数選べます）</div>
          <div className="rfb-chips">
            {CHAT_FEEDBACK_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                className={`rfb-chip${reasons.includes(r) ? ' is-on' : ''}`}
                aria-pressed={reasons.includes(r)}
                onClick={() => toggleReason(r)}
              >
                {r}
              </button>
            ))}
          </div>
          <textarea
            className="rfb-comment"
            rows={2}
            maxLength={CHAT_FEEDBACK_COMMENT_MAX}
            placeholder="ひと言（任意）"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button"
            className="chip chip--primary rfb-send"
            disabled={saving || !canSend}
            onClick={() => save('down', reasons, comment.trim())}
          >
            {saving ? '送信中…' : '送信'}
          </button>
        </div>
      )}
      {error && <div className="rfb-error">{error}</div>}
    </div>
  );
}
